import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import {
  getPolicyChapters, subscribePolicyChapters, savePolicyChapters, resetPolicyChapters,
  newPolicyChapter, isBuiltInChapter, chapterTitle, type PolicyChapter,
} from "../../data/policies";
import { logAudit } from "../lib/audit";

function PolicyEditModal({ lang, chapter, isNew, onClose, onSave }: { lang: Lang; chapter: PolicyChapter; isNew: boolean; onClose: () => void; onSave: (c: PolicyChapter) => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [draft, setDraft] = useState<PolicyChapter>(chapter);
  const set = (key: keyof PolicyChapter, val: string) => setDraft((d) => ({ ...d, [key]: val }));
  const builtIn = !isNew && isBuiltInChapter(chapter.id);

  function save() {
    if (!draft.id.trim()) return toast.error(t("لازم يكون للفصل معرّف (ID).", "The chapter needs an id."));
    if (!draft.titleAr.trim() || !draft.titleEn.trim()) return toast.error(t("يرجى تعبئة العنوان بالعربية والإنجليزية", "Please fill the title in both Arabic and English"));
    onSave(draft);
  }

  return (
    <div className="kd-admin-modal-backdrop" onClick={onClose}>
      <div className="kd-admin-modal kd-admin-modal-wide" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="kd-admin-modal-header">
          <h2>{isNew ? t("إضافة فصل جديد", "Add New Chapter") : t("تعديل الفصل", "Edit Chapter")}</h2>
          <button className="kd-admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="kd-admin-modal-body">
          <div className="kd-admin-grid-3">
            <label><span>{t("الأيقونة", "Icon")}</span><input type="text" value={draft.icon} onChange={(e) => set("icon", e.target.value)} maxLength={4} style={{ fontSize: "1.2rem", textAlign: "center" }} /></label>
            <label style={{ gridColumn: "span 2" }}>
              <span>{t("المعرّف (ID)", "Identifier (ID)")}{builtIn && <small className="kd-admin-default-tag"> · {t("افتراضي", "built-in")}</small>}</span>
              <input
                type="text"
                value={draft.id}
                onChange={(e) => set("id", e.target.value.trim().replace(/[^a-z0-9-]/gi, "-").toLowerCase())}
                disabled={builtIn}
                placeholder="attendance, travel, ..."
                style={{ fontFamily: "var(--kd-font-en)" }}
              />
            </label>
          </div>
          <div className="kd-admin-grid-2">
            <label><span>{t("العنوان بالعربية", "Title (Arabic)")}</span><input type="text" value={draft.titleAr} onChange={(e) => set("titleAr", e.target.value)} dir="rtl" /></label>
            <label><span>{t("العنوان بالإنجليزية", "Title (English)")}</span><input type="text" value={draft.titleEn} onChange={(e) => set("titleEn", e.target.value)} dir="ltr" /></label>
          </div>
          <div className="kd-admin-grid-2">
            <label><span>{t("المقدمة بالعربية", "Intro (Arabic)")}</span><textarea rows={2} value={draft.introAr} onChange={(e) => set("introAr", e.target.value)} dir="rtl" /></label>
            <label><span>{t("المقدمة بالإنجليزية", "Intro (English)")}</span><textarea rows={2} value={draft.introEn} onChange={(e) => set("introEn", e.target.value)} dir="ltr" /></label>
          </div>
          <label>
            <span>{t("محتوى الفصل بالعربية (HTML)", "Chapter body (Arabic, HTML)")}</span>
            <textarea
              className="kd-admin-html-source"
              rows={12}
              value={draft.bodyHtmlAr}
              onChange={(e) => set("bodyHtmlAr", e.target.value)}
              dir="ltr"
              spellCheck={false}
              placeholder={t("اكتب كود HTML هنا...", "Write HTML markup here...")}
            />
          </label>
          <label>
            <span>{t("محتوى الفصل بالإنجليزية (HTML)", "Chapter body (English, HTML)")}</span>
            <textarea
              className="kd-admin-html-source"
              rows={12}
              value={draft.bodyHtmlEn}
              onChange={(e) => set("bodyHtmlEn", e.target.value)}
              dir="ltr"
              spellCheck={false}
              placeholder="Write HTML markup here..."
            />
          </label>
          <p className="kd-admin-hint">
            {t(
              "هذا الحقل يحرّر كود HTML مباشرة (وليس محررًا مرئيًا) — بعض فصول السياسات تحتوي جداول متقدّمة وصورًا، ومحرر النصوص المنسّقة كان يفقدها عند الحفظ. عدّل بحذر وتأكد من إغلاق كل وسم بشكل صحيح.",
              "This field edits raw HTML markup directly (not a WYSIWYG editor) — some policy chapters contain advanced tables and images that the rich-text editor was silently losing on save. Edit carefully and make sure every tag is properly closed.",
            )}
          </p>
        </div>
        <div className="kd-admin-modal-footer">
          <button className="kd-btn-secondary" onClick={onClose}>{t("إلغاء", "Cancel")}</button>
          <button className="kd-btn-primary kd-btn-gold" onClick={save}>{t("حفظ", "Save")}</button>
        </div>
      </div>
    </div>
  );
}

/** Policies & Procedures editor tab — same freedom as Career Paths / Q&A: add,
 *  reorder, edit, or delete any chapter without touching code. Chapter bodies
 *  are rich HTML edited via the same RichTextEditor used for Q&A answers. */
export function PoliciesTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [chapters, setChapters] = useState<PolicyChapter[]>(() => getPolicyChapters());
  const [editing, setEditing] = useState<{ chapter: PolicyChapter; isNew: boolean } | null>(null);
  useEffect(() => subscribePolicyChapters(() => setChapters([...getPolicyChapters()])), []);

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= chapters.length) return;
    const next = [...chapters];
    [next[i], next[j]] = [next[j], next[i]];
    saveChapters(next, false);
  };
  const remove = (i: number) => {
    if (!confirm(t("سيتم حذف هذا الفصل نهائياً. متأكد؟", "This chapter will be permanently deleted. Are you sure?"))) return;
    const target = chapters[i];
    saveChapters(chapters.filter((_, idx) => idx !== i), true, () => logAudit("config_imported", `Policy chapter deleted: ${target.id}`));
  };

  async function saveChapters(next: PolicyChapter[], announce = true, after?: () => void) {
    setChapters(next);
    const res = await savePolicyChapters(next);
    if (res.ok) {
      if (announce) toast.success(t("تم الحفظ", "Saved"));
      after?.();
    } else {
      toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
    }
  }

  function onSaveChapter(chapter: PolicyChapter) {
    const isNew = editing?.isNew;
    let next: PolicyChapter[];
    if (isNew) {
      if (chapters.some((c) => c.id === chapter.id)) {
        toast.error(t(`معرّف مكرر: ${chapter.id}`, `Duplicate id: ${chapter.id}`));
        return;
      }
      next = [...chapters, chapter];
    } else {
      next = chapters.map((c) => (c.id === editing?.chapter.id ? chapter : c));
    }
    saveChapters(next, true, () => logAudit(isNew ? "config_imported" : "config_imported", `Policy chapter ${isNew ? "added" : "updated"}: ${chapter.id}`));
    setEditing(null);
  }

  async function reset() {
    if (!confirm(t("استعادة فصول السياسات الافتراضية؟ أي تعديلات مخصصة ستُفقد.", "Restore default policy chapters? Custom changes will be lost."))) return;
    const res = await resetPolicyChapters();
    res.ok ? toast.success(t("تمت الاستعادة", "Reset")) : toast.error(t("تعذّر الاستعادة: ", "Reset failed: ") + (res.error || ""));
  }

  return (
    <div>
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary kd-btn-gold" onClick={() => setEditing({ chapter: newPolicyChapter(), isNew: true })}>+ {t("إضافة فصل جديد", "Add New Chapter")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={reset}>⟲ {t("استعادة الافتراضي", "Reset to Defaults")}</button>
        <span className="kd-admin-path-count">
          {t("عدد الفصول:", "Total chapters:")} <strong>{chapters.length}</strong>
        </span>
      </div>
      <p className="kd-admin-hint">
        {t(
          "هذه هي فصول سياسات وإجراءات كادن التي يقرأها كل موظف جديد في الخطوة الرابعة من الرحلة. يمكنك تعديل أي فصل أو إضافة فصول جديدة أو حذفها دون الحاجة لتعديل الكود.",
          "These are the Kaden policy chapters every new employee reads at step 4 of the journey. Edit any chapter, or add/remove chapters, without touching code.",
        )}
      </p>
      <div className="kd-admin-table-wrap">
        <table className="kd-admin-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th style={{ width: 50 }}></th>
              <th>{t("الفصل", "Chapter")}</th>
              <th>{t("المقدمة", "Intro")}</th>
              <th style={{ width: 160 }}>{t("إجراءات", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {chapters.map((c, i) => (
              <tr key={c.id}>
                <td>{i + 1}</td>
                <td style={{ fontSize: "1.3rem", textAlign: "center" }}>{c.icon}</td>
                <td>
                  <strong>{chapterTitle(c, lang)}</strong>
                  <br />
                  <small style={{ color: "var(--kd-text-muted)", fontFamily: "var(--kd-font-en)" }}>{c.id}</small>
                </td>
                <td>
                  <small style={{ color: "var(--kd-text-muted)" }}>{(lang === "ar" ? c.introAr : c.introEn).slice(0, 90)}{(lang === "ar" ? c.introAr : c.introEn).length > 90 ? "…" : ""}</small>
                </td>
                <td>
                  <div className="kd-admin-actions-cell">
                    <button className="kd-admin-action-btn" onClick={() => move(i, -1)} disabled={i === 0} title={t("أعلى", "Up")}>↑</button>
                    <button className="kd-admin-action-btn" onClick={() => move(i, 1)} disabled={i === chapters.length - 1} title={t("أسفل", "Down")}>↓</button>
                    <button className="kd-admin-action-btn" onClick={() => setEditing({ chapter: c, isNew: false })} title="Edit">✎</button>
                    <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => remove(i)} title="Delete">🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PolicyEditModal
          lang={lang}
          chapter={editing.chapter}
          isNew={editing.isNew}
          onClose={() => setEditing(null)}
          onSave={onSaveChapter}
        />
      )}
    </div>
  );
}
